import { IESDoc } from "../../../app.interface";
import axiosInstance from "../../../middlewares/axiosInstance";
import { AxiosResponse } from "axios";

interface ListParams {
  searchQuery?: string;
  filters?: any[];
  sortField?: string;
  sortOrder?: "asc" | "desc";
  pageIndex?: number;
  pageSize?: number;
}

interface ListResponse {
  data: IESDoc[];
  total: number;
  pageIndex: number;
  pageSize: number;
}

interface PurchaseRequestItem {
  item_id: string;
  item_name: string;
  uom: string;
  quantity: number;
  reason: string;
  urgency: "Low" | "Medium" | "High";
}

interface PurchaseRequestData {
  items: PurchaseRequestItem[];
  status?: string;
  created_at?: Date;
  created_by?: string;
  _id?: string;
}

export interface ActivityLog {
  action:
    | "created"
    | "updated"
    | "submitted"
    | "approved"
    | "rejected"
    | "reopened";
  changed_by: string;
  changed_by_email?: string;
  previous_status?: string;
  new_status: string;
  timestamp: number;
  details?: string;
  notes?: string;
  rejection_reason?: string;
}

export interface ActivityLogsResponse {
  data: ActivityLog[];
  total: number;
  pageIndex: number;
  pageSize: number;
}

class PurchaseRequestService {
  async listPurchaseRequests(params: ListParams): Promise<ListResponse> {
    const response = await axiosInstance.post(
      "/business-admin/purchase-requests/list",
      {
        ...params,
      }
    );
    return response.data.data;
  }

  async getPurchaseRequest(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/purchase-requests/get", {
      _id: id,
    });
  }

  async createPurchaseRequest(
    data: PurchaseRequestData
  ): Promise<AxiosResponse<any>> {
    const dataToSend = {
      items: data.items.map((item) => ({
        ...item,
        urgency: item.urgency.toLowerCase(),
      })),
    };
    const response = await axiosInstance.post(
      "/business-admin/purchase-requests/create",
      dataToSend
    );
    return response.data;
  }

  async updatePurchaseRequest(
    data: PurchaseRequestData
  ): Promise<AxiosResponse<any>> {
    const dataToSend = {
      ...data,
      items: data.items.map((item) => ({
        ...item,
        urgency: item.urgency.toLowerCase(),
      })),
    };
    const response = await axiosInstance.post(
      "/business-admin/purchase-requests/update",
      dataToSend
    );
    return response.data;
  }

  async deletePurchaseRequest(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/purchase-requests/delete", {
      _id: id,
    });
  }

  async approvePurchaseRequest(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/purchase-requests/approve", {
      _id: id,
    });
  }

  async rejectPurchaseRequest(
    id: string,
    reason?: string
  ): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/purchase-requests/reject", {
      _id: id,
      rejection_reason: reason,
    });
  }

  // Get Item Master list for dropdown
  async getItemMaster(params?: {
    searchQuery?: string;
    pageIndex?: number;
    pageSize?: number;
  }): Promise<{
    data: Array<{
      _id: string;
      name: string;
      uom: string;
      category?: string;
    }>;
    total: number;
  }> {
    const response = await axiosInstance.post(
      "/business-admin/item-master/list",
      {
        searchQuery: params?.searchQuery || "",
        pageIndex: params?.pageIndex || 0,
        pageSize: params?.pageSize || 100,
      }
    );
    return response.data.data;
  }

  async submitPurchaseRequest(
    data: PurchaseRequestData
  ): Promise<AxiosResponse<any>> {
    const dataToSend = {
      ...data,
      items: data.items.map((item) => ({
        ...item,
        urgency: item.urgency.toLowerCase(),
      })),
    };
    const response = await axiosInstance.post(
      "/business-admin/purchase-requests/submit",
      dataToSend
    );
    return response.data;
  }

  async reopenPurchaseRequest(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/purchase-requests/reopen", {
      _id: id,
    });
  }

  async getPurchaseRequestActivityLogs(
    purchaseRequestId: string,
    params?: {
      pageIndex?: number;
      pageSize?: number;
      action?: string;
      startDate?: number;
      endDate?: number;
    }
  ): Promise<ActivityLogsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.pageIndex !== undefined)
      queryParams.append("pageIndex", params.pageIndex.toString());
    if (params?.pageSize !== undefined)
      queryParams.append("pageSize", params.pageSize.toString());
    if (params?.action) queryParams.append("action", params.action);
    if (params?.startDate)
      queryParams.append("startDate", params.startDate.toString());
    if (params?.endDate)
      queryParams.append("endDate", params.endDate.toString());

    const response = await axiosInstance.get(
      `/business-admin/purchase-requests/${purchaseRequestId}/activity-logs?${queryParams}`
    );

    return response.data.data;
  }
}

export const purchaseRequestService = new PurchaseRequestService();
